import * as React from "react";
import Box from "@mui/material/Box";
import { filterType } from "../../redux-store/types/api-types";
import { FilterSkeleton } from "../../components/dashboard/chart/CustomePieChart";
import { CustomDropdownOrangeIcon, DialogCloseIcon, SearchIcon } from "../../assets/SvgIcons";
import { IconButton, InputAdornment, TextField, useTheme, Popover, ListItemButton, Typography } from "@mui/material";
import { whiteSpaceOnlyRegex } from "../../utils/regex";

type FilterProps = {
    options: Array<filterType>,
    defaultValue: number | string,
    IsLoading: boolean,
    setFilterValue?: React.Dispatch<React.SetStateAction<string | number>>,
    filterFunction?: (value: string | number) => void,
    searchable?: boolean;
}

export default function CustomDropdown({
    options,
    defaultValue,
    IsLoading,
    setFilterValue,
    filterFunction,
    searchable = false
}: FilterProps): JSX.Element {
    const theme = useTheme();
    const [value, setValue] = React.useState<string | number>(defaultValue);
    const [searchText, setSearchText] = React.useState<string>('');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [filteredOptions, setFilteredOptions] = React.useState<filterType[]>(options);
    const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
    const optionRefs = React.useRef<(HTMLElement | null)[]>([]);
    const listboxRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue);
        }
    }, [defaultValue]);

    React.useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    const open = Boolean(anchorEl);

    // A8: Focus the first option when the popover opens so keyboard navigation works immediately.
    React.useEffect(() => {
        if (open) {
            setTimeout(() => {
                if (optionRefs.current[0]) {
                    optionRefs.current[0].focus();
                    setFocusedIndex(0);
                } else {
                    listboxRef.current?.focus();
                }
            }, 0);
        }
    }, [open]);

    // CORR-163: Reset keyboard focus whenever the visible option list changes
    // (e.g. user types in the search box or clears it). When the list empties,
    // move focus to the listbox container so ArrowDown/Up become safe no-ops
    // until results return. When results reappear, jump to the first option.
    React.useEffect(() => {
        if (!open) return;
        setTimeout(() => {
            if (filteredOptions.length > 0) {
                optionRefs.current[0]?.focus();
                setFocusedIndex(0);
            } else {
                setFocusedIndex(-1);
                listboxRef.current?.focus();
            }
        }, 0);
    // `open` is intentionally omitted: the effect above handles the initial open.
    // This one only needs to fire when the filtered list itself changes.
    }, [filteredOptions]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setSearchText('');
        setFocusedIndex(-1);
        // Move focus to the first option (or the listbox) once the popover renders
        setTimeout(() => {
            if (optionRefs.current[0]) {
                optionRefs.current[0].focus();
                setFocusedIndex(0);
            } else {
                listboxRef.current?.focus();
            }
        }, 0);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchText('');
        setFilteredOptions(options);
        setFocusedIndex(-1);
    };

    const handleClearSearch = () => {
        setSearchText('');
        setFilteredOptions(options);
    };

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        const query = event.target.value.toLowerCase();
        setSearchText(event.target.value);
        setFocusedIndex(-1);

        const filtered = options.filter(option =>
            (option.label as string).toLowerCase().includes(query)
        );
        setFilteredOptions(filtered);
    };

    const handleSelect = (selectedValue: string | number) => {
        setValue(selectedValue);
        setFilterValue && setFilterValue(selectedValue);
        filterFunction && filterFunction(selectedValue as string);
        handleClose();
    };

    if (IsLoading) return <FilterSkeleton />;

    return (
        <>
            {/* Main Dropdown Button */}
            <Box
                onClick={handleClick}
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={`Select option, current: ${options?.find(opt => opt.value === value)?.label || ''}`}
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        handleClick(e as unknown as React.MouseEvent<HTMLElement>);
                    }
                }}
                sx={{
                    ...theme.typography.body_500,
                    color: theme.palette.customColors.subtitle,
                    cursor: 'pointer',
                    padding: { xs: "4px 2px", sm: "7px 5px" },
                    borderBottom: `1px dashed ${theme.palette.text.secondary}B2`,
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'transparent',
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                    },
                }}
            >
                <Typography variant="body_500" sx={{ flex: 1 }}>
                    {options?.find(opt => opt.value === value)?.label || ''}
                </Typography>
                <CustomDropdownOrangeIcon />
            </Box>

            {/* Search Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            padding: "2px",
                            borderRadius: "12px",

                            "& .MuiListItemButton-root": {
                                padding: "6px",
                                borderRadius: "8px",
                                ...theme.typography.body_500,
                                color: theme.palette.customColors.subtitle,
                                my: 0.4,
                                "&.Mui-selected": {
                                    color: theme.palette.customColors.subtitle,
                                },
                                [theme.breakpoints.down("sm")]: {
                                    padding: "4px 6px",
                                    fontSize: "0.8rem",
                                    minHeight: "32px",
                                },
                            },
                            "& .MuiListItemButton-root:hover": {
                                color: theme.palette.customColors.subtitle,
                            },
                        },
                    }
                }}
            >
                {/* Search Input */}
                {searchable && (
                    <Box sx={{ p: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearchTextChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText?.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onMouseDown={handleClearSearch}
                                            aria-label="Clear search"
                                        >
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '40px',
                                    borderRadius: '25px',
                                },
                                '& input': {
                                    paddingLeft: '10px',
                                    ...theme.typography.body_500,
                                }
                            }}
                        />
                    </Box>
                )}

                {/* Options List */}
                <Box
                    ref={listboxRef}
                    role="listbox"
                    aria-label="Options"
                    tabIndex={0}
                    sx={{ maxHeight: 250, overflow: 'auto' }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            // guard: skip keyboard navigation when no options are visible
                            if (filteredOptions.length === 0) return;
                            const nextIndex = Math.min(focusedIndex + 1, filteredOptions.length - 1);
                            setFocusedIndex(nextIndex);
                            optionRefs.current[nextIndex]?.focus();
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            // guard: skip keyboard navigation when no options are visible
                            if (filteredOptions.length === 0) return;
                            const prevIndex = Math.max(focusedIndex - 1, 0);
                            setFocusedIndex(prevIndex);
                            optionRefs.current[prevIndex]?.focus();
                        } else if (e.key === 'Enter' && focusedIndex >= 0) {
                            e.preventDefault();
                            handleSelect(filteredOptions[focusedIndex]?.value as string | number);
                        } else if (e.key === 'Escape') {
                            handleClose();
                        }
                    }}
                >
                    {filteredOptions?.length > 0 ? (
                        filteredOptions?.map((option, index) => (
                            <ListItemButton
                                key={option.value}
                                role="option"
                                aria-selected={value === option.value}
                                tabIndex={focusedIndex === index ? 0 : -1}
                                ref={(el: HTMLElement | null) => { optionRefs.current[index] = el; }}
                                selected={value === option.value}
                                onClick={() => handleSelect((option.value as any))}
                                sx={{
                                    padding: '7px 5px',
                                    borderRadius: '8px',
                                    margin: '4px',
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary[200],
                                    },
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    }
                                }}
                            >
                                <Typography variant="body_500">{option.label}</Typography>
                            </ListItemButton>
                        ))
                    ) : (
                        <Typography variant="body_500" sx={{ p: 1, textAlign: 'center', color: 'text.secondary' }}>
                            No options found
                        </Typography>
                    )}
                </Box>
            </Popover>
        </>
    );
}
